/**
 * Centralized Date Formatting Utility
 * 
 * Single source of truth for all date/time display in the app.
 * Uses native Intl.DateTimeFormat — zero external dependencies.
 * Memoizes formatter instances for performance.
 * 
 * Reads timezone from ProfileCache (localStorage) when no explicit timezone is passed.
 */


const formatterCache = new Map();

const getFormatter = (timezone, options) => {
  const key = `${timezone}|${JSON.stringify(options)}`;
  if (!formatterCache.has(key)) {
    formatterCache.set(key, new Intl.DateTimeFormat(undefined, { ...options, timeZone: timezone }));
  }
  return formatterCache.get(key);
};

// ── Timezone Resolution ──────────────────────────────────────────

/**
 * Get the browser's auto-detected timezone.
 * @returns {string}
 */
export const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

/**
 * Get the current user's timezone from ProfileCache.
 * Falls back to browser auto-detect if not set.
 * @returns {string} IANA timezone string
 */
export const getUserTimezone = () => {
  try {
    // Try IAM console first, then admin console
    const cacheKeys = ['profile_cache_iam-console', 'profile_cache_admin-console'];
    for (const key of cacheKeys) {
      const cached = localStorage.getItem(key);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.data?.timezone) {
          return parsed.data.timezone;
        }
      }
    }
  } catch {
    // Ignore parse errors — fall back to browser timezone
  }
  return getBrowserTimezone();
};

// ── Date Formatting Functions ────────────────────────────────────

/**
 * Format an ISO timestamp as full date + time.
 * @param {string} isoString - ISO 8601 timestamp (e.g., "2026-04-20T05:18:34Z")
 * @param {string} [timezone] - IANA timezone (e.g., "America/New_York"). Defaults to user's timezone.
 * @returns {string} e.g., "Apr 20, 2026, 10:48 AM"
 */
export const formatDateTime = (isoString, timezone) => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return String(isoString);
    
    const tz = timezone || getUserTimezone();
    const formatter = getFormatter(tz, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    return formatter.format(date);
  } catch {
    return String(isoString);
  }
};

/**
 * Format an ISO timestamp as date only (no time).
 * @param {string} isoString - ISO 8601 timestamp
 * @param {string} [timezone] - IANA timezone. Defaults to user's timezone.
 * @returns {string} e.g., "Apr 20, 2026"
 */
export const formatDateOnly = (isoString, timezone) => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return String(isoString);
    
    const tz = timezone || getUserTimezone();
    const formatter = getFormatter(tz, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    return formatter.format(date);
  } catch {
    return String(isoString);
  }
};

/**
 * Format an ISO timestamp as a relative time string.
 * @param {string} isoString - ISO 8601 timestamp
 * @returns {string} e.g., "2h ago", "3d ago", "Just now"
 */
export const formatRelativeTime = (isoString) => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return String(isoString);
    
    const now = Date.now();
    const diffMs = now - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    if (diffSec < 60) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay < 30) return `${diffDay}d ago`;

    // For older dates, fall back to formatted date
    return formatDateOnly(isoString);
  } catch {
    return String(isoString);
  }
};

// ── Timezone Display Helpers ─────────────────────────────────────

/**
 * Get a human-readable label for a timezone.
 * @param {string} timezone - IANA timezone string
 * @returns {string} e.g., "Asia/Kolkata (UTC+5:30)"
 */
export const getTimezoneDisplayLabel = (timezone) => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find(p => p.type === 'timeZoneName');
    const offset = offsetPart ? offsetPart.value : '';
    return `${timezone.replace(/_/g, ' ')} (${offset})`;
  } catch {
    return timezone;
  }
};

// ── Curated Timezone List ────────────────────────────────────────
// ~40 common timezones grouped by region, covering major global regions.

export const TIMEZONE_LIST = [
  // Americas
  { value: 'America/New_York', label: 'New York', region: 'Americas' },
  { value: 'America/Chicago', label: 'Chicago', region: 'Americas' },
  { value: 'America/Denver', label: 'Denver', region: 'Americas' },
  { value: 'America/Los_Angeles', label: 'Los Angeles', region: 'Americas' },
  { value: 'America/Anchorage', label: 'Anchorage', region: 'Americas' },
  { value: 'America/Toronto', label: 'Toronto', region: 'Americas' },
  { value: 'America/Vancouver', label: 'Vancouver', region: 'Americas' },
  { value: 'America/Mexico_City', label: 'Mexico City', region: 'Americas' },
  { value: 'America/Sao_Paulo', label: 'São Paulo', region: 'Americas' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires', region: 'Americas' },
  { value: 'America/Bogota', label: 'Bogotá', region: 'Americas' },

  // Europe
  { value: 'Europe/London', label: 'London', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Paris', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Berlin', region: 'Europe' },
  { value: 'Europe/Madrid', label: 'Madrid', region: 'Europe' },
  { value: 'Europe/Rome', label: 'Rome', region: 'Europe' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam', region: 'Europe' },
  { value: 'Europe/Zurich', label: 'Zurich', region: 'Europe' },
  { value: 'Europe/Stockholm', label: 'Stockholm', region: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow', region: 'Europe' },
  { value: 'Europe/Istanbul', label: 'Istanbul', region: 'Europe' },
  { value: 'Europe/Athens', label: 'Athens', region: 'Europe' },
  { value: 'Europe/Warsaw', label: 'Warsaw', region: 'Europe' },

  // Asia
  { value: 'Asia/Kolkata', label: 'India (Kolkata)', region: 'Asia' },
  { value: 'Asia/Dubai', label: 'Dubai', region: 'Asia' },
  { value: 'Asia/Riyadh', label: 'Riyadh', region: 'Asia' },
  { value: 'Asia/Singapore', label: 'Singapore', region: 'Asia' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong', region: 'Asia' },
  { value: 'Asia/Shanghai', label: 'Shanghai', region: 'Asia' },
  { value: 'Asia/Tokyo', label: 'Tokyo', region: 'Asia' },
  { value: 'Asia/Seoul', label: 'Seoul', region: 'Asia' },
  { value: 'Asia/Bangkok', label: 'Bangkok', region: 'Asia' },
  { value: 'Asia/Jakarta', label: 'Jakarta', region: 'Asia' },
  { value: 'Asia/Karachi', label: 'Karachi', region: 'Asia' },
  { value: 'Asia/Dhaka', label: 'Dhaka', region: 'Asia' },
  { value: 'Asia/Tehran', label: 'Tehran', region: 'Asia' },

  // Oceania
  { value: 'Australia/Sydney', label: 'Sydney', region: 'Oceania' },
  { value: 'Australia/Melbourne', label: 'Melbourne', region: 'Oceania' },
  { value: 'Australia/Perth', label: 'Perth', region: 'Oceania' },
  { value: 'Pacific/Auckland', label: 'Auckland', region: 'Oceania' },

  // Africa
  { value: 'Africa/Cairo', label: 'Cairo', region: 'Africa' },
  { value: 'Africa/Lagos', label: 'Lagos', region: 'Africa' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg', region: 'Africa' },
  { value: 'Africa/Nairobi', label: 'Nairobi', region: 'Africa' },

  // UTC
  { value: 'UTC', label: 'UTC', region: 'UTC' },
];

/**
 * Clear the formatter cache. 
 * Call this when timezone changes to ensure fresh formatters.
 */
export const clearFormatterCache = () => {
  formatterCache.clear();
};